import { html, CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { BlocksElement } from '../../BlocksElement';
import styles from '../icon.styles';
import type { IconColorType, IconSize } from './icon.model';

/**
 * Wifi component
 * @csspart base- The component's base wrapper
 */
export class BlocksIconWifi extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Icon color variant
   * <cat-text-passage size="sm">
   * <ul>
   * <li>No color defined renders an icon with the currentColor of its parent element</li>
   * <li>**content-strong** renders an icon with the cat-theme-color-content-strong color token</li>
   * <li>**knockout** renders an icon with the cat-theme-color-content-knockout color token</li>
   * <li>**knockout-brand** renders a renders an icon with the cat-theme-color-content-knockout-brand color token</li>
   * <li>**information** renders an icon with the cat-theme-color-content-utility-information color token</li>
   * <li>**success** renders an icon with the cat-theme-color-content-utility-success color token</li>
   * <li>**warning** renders a renders an icon with the cat-theme-color-content-utility-warning color token</li>
   * <li>**error** renders an icon with the cat-theme-color-content-utility-error color token</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  color: IconColorType;

  /**
   * Title
   * 1) Icons should always have associated text. Use this to supply `aria-label` to icon if it stands by itself without any visual/visually-hidden text
   */
  @property({ type: String })
  iconTitle: string | undefined;

  /**
   * Size property
   * - Default size is 16px
   * - **sm** renders a larger size than default (12px)
   * - **lg** renders a larger size than default (24px)
   * - **xl** renders a larger size than the lg variant (32px);
   */
  @property({ type: String })
  size: IconSize;

  connectedCallback() {
    super.connectedCallback();
    this.classList.add('cat-icon');
  }

  render() {
    const componentClassName = this.componentClassNames('cat-c-icon', {
      'cat-c-icon--sm': this.size === 'sm',
      'cat-c-icon--lg': this.size === 'lg',
      'cat-c-icon--xl': this.size === 'xl',
      'cat-c-icon--content-strong': this.color === 'content-strong',
      'cat-c-icon--knockout': this.color === 'knockout',
      'cat-c-icon--knockout-brand': this.color === 'knockout-brand',
      'cat-c-icon--information': this.color === 'information',
      'cat-c-icon--success': this.color === 'success',
      'cat-c-icon--warning': this.color === 'warning',
      'cat-c-icon--error': this.color === 'error'
    });

    return html`
      <span part="base" class="${componentClassName}" role="presentation">
        <svg
          aria-hidden="${!this.iconTitle}"
          aria-label="${ifDefined(this.iconTitle)}"
          role="${this.iconTitle && 'img'}"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M6.79684 5.59694C8.44654 4.91336 10.2148 4.56152 12.0005 4.56152C13.7862 4.56152 15.5544 4.91336 17.2041 5.59694C18.8538 6.28052 20.3526 7.28244 21.615 8.54546C21.9078 8.83843 22.3827 8.83855 22.6757 8.54573C22.9686 8.25291 22.9688 7.77804 22.6759 7.48507C21.2743 6.08268 19.61 4.9702 17.7783 4.2112C15.9466 3.45219 13.9832 3.06152 12.0005 3.06152C10.0177 3.06152 8.05437 3.45219 6.22264 4.2112C4.3909 4.9702 2.72666 6.08268 1.325 7.48507C1.03218 7.77804 1.03231 8.25291 1.32528 8.54573C1.61825 8.83855 2.09312 8.83843 2.38594 8.54546C3.6483 7.28244 5.14715 6.28052 6.79684 5.59694ZM12.0081 8.65332C10.7594 8.65332 9.52308 8.90018 8.37018 9.37969C7.21727 9.85921 6.17053 10.5619 5.29017 11.4474C4.99813 11.7412 4.52326 11.7425 4.22952 11.4505C3.93577 11.1585 3.93439 10.6836 4.22643 10.3898C5.24619 9.36414 6.45867 8.55015 7.79414 7.99471C9.1296 7.43927 10.5617 7.15332 12.0081 7.15332C13.4544 7.15332 14.8865 7.43927 16.222 7.99471C17.5575 8.55015 18.77 9.36414 19.7897 10.3898C20.0818 10.6836 20.0804 11.1585 19.7866 11.4505C19.4929 11.7425 19.018 11.7412 18.726 11.4474C17.8456 10.5619 16.7989 9.85921 15.646 9.37969C14.4931 8.90018 13.2567 8.65332 12.0081 8.65332ZM10.2215 13.0286C10.7994 12.7848 11.4202 12.6592 12.0474 12.6592C12.6746 12.6592 13.2955 12.7848 13.8734 13.0286C14.4513 13.2724 14.9745 13.6295 15.4121 14.0788C15.7011 14.3755 16.176 14.3818 16.4727 14.0928C16.7694 13.8037 16.7756 13.3289 16.4866 13.0322C15.9092 12.4394 15.2189 11.9683 14.4565 11.6466C13.6941 11.3249 12.8749 11.1592 12.0474 11.1592C11.2199 11.1592 10.4008 11.3249 9.63836 11.6466C8.87594 11.9683 8.18566 12.4394 7.60826 13.0322C7.31924 13.3289 7.32547 13.8037 7.62219 14.0928C7.9189 14.3818 8.39374 14.3755 8.68276 14.0788C9.1204 13.6295 9.6436 13.2724 10.2215 13.0286ZM10.7061 18.1442C10.7061 17.4292 11.2857 16.8496 12.0007 16.8496C12.7157 16.8496 13.2953 17.4292 13.2953 18.1442C13.2953 18.8592 12.7157 19.4388 12.0007 19.4388C11.2857 19.4388 10.7061 18.8592 10.7061 18.1442ZM12.0007 15.3496C10.4572 15.3496 9.20605 16.6008 9.20605 18.1442C9.20605 19.6876 10.4572 20.9388 12.0007 20.9388C13.5441 20.9388 14.7953 19.6876 14.7953 18.1442C14.7953 16.6008 13.5441 15.3496 12.0007 15.3496Z"
          />
        </svg>
      </span>
    `;
  }
}

if (customElements.get('cat-icon-wifi') === undefined) {
  customElements.define('cat-icon-wifi', BlocksIconWifi);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-icon-wifi': BlocksIconWifi;
  }
}
